import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Grid, Paper, Typography, useTheme } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { tokens } from "../../theme";
import IconButton from "../../components/ui/IconButton";
import Chip from "../../components/ui/Chip";
import Paginator, { PaginationProps } from "../../components/ui/Paginator";
import {
  addJob,
  getJobs,
  getSubmissions,
  toggleJobActive,
  updateJob,
  updateSubmissionStatus,
} from "./services/jobsStorage";
import MetricsCards from "./components/admin/MetricsCards";
import ApplicantsTable from "./components/admin/ApplicantsTable";
import CandidateDetailModal from "./components/admin/CandidateDetailModal";
import NewJobModal from "./components/admin/NewJobModal";
import type { ApplicationSubmission, JobPosition } from "./types";

function extractErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === "object") {
    const message = Object.values(data).flat().join(" ");
    if (message) return message;
  }
  return fallback;
}

const JobsAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [submissions, setSubmissions] = useState<ApplicationSubmission[]>([]);
  const [jobs, setJobs] = useState<JobPosition[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<ApplicationSubmission | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isNewJobModalOpen, setIsNewJobModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<JobPosition | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [jobsPagination, setJobsPagination] = useState<PaginationProps>({ page: 0, pageSize: 10 });

  const loadData = async () => {
    setLoadError(null);
    try {
      const [loadedSubmissions, loadedJobs] = await Promise.all([getSubmissions(), getJobs()]);
      setSubmissions(loadedSubmissions);
      setJobs(loadedJobs);
    } catch (error) {
      setLoadError(extractErrorMessage(error, "Unable to load recruitment data. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    const applicationId = searchParams.get("application");
    if (!applicationId || submissions.length === 0) return;
    const linkedSubmission = submissions.find((submission) => submission.id === applicationId);
    if (!linkedSubmission) return;
    setSelectedSubmission(linkedSubmission);
    setIsDetailModalOpen(true);
  }, [searchParams, submissions]);

  const handleSelectSubmission = (sub: ApplicationSubmission) => {
    setSelectedSubmission(sub);
    setIsDetailModalOpen(true);
  };

  const handleCloseSubmission = () => {
    setIsDetailModalOpen(false);
    if (!searchParams.has("application")) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("application");
    setSearchParams(nextParams, { replace: true });
  };

  const handleUpdateStatus = async (submissionId: string, status: ApplicationSubmission["status"]) => {
    try {
      const updated = await updateSubmissionStatus(submissionId, status);
      setSelectedSubmission(updated);
      await loadData();
    } catch (error) {
      setLoadError(extractErrorMessage(error, "Unable to update the application status. Please try again."));
    }
  };

  const handleAddJob = async (jobData: Omit<JobPosition, "id" | "createdAt" | "active">) => {
    try {
      await addJob(jobData);
      await loadData();
    } catch (error) {
      setLoadError(extractErrorMessage(error, "Unable to create the job posting. Please try again."));
    }
  };

  const handleUpdateJob = async (id: string, jobData: Omit<JobPosition, "id" | "createdAt" | "active">) => {
    try {
      await updateJob(id, jobData);
      await loadData();
    } catch (error) {
      setLoadError(extractErrorMessage(error, "Unable to update the job posting. Please try again."));
    }
  };

  const handleToggleActive = async (job: JobPosition) => {
    try {
      const updated = await toggleJobActive(job.id, !job.active);
      setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
      setEditingJob((prev) => (prev && prev.id === updated.id ? updated : prev));
    } catch (error) {
      setLoadError(extractErrorMessage(error, "Unable to update the job's active status. Please try again."));
    }
  };

  const openCreateJobModal = () => {
    setEditingJob(null);
    setIsNewJobModalOpen(true);
  };

  const openEditJobModal = (job: JobPosition) => {
    setEditingJob(job);
    setIsNewJobModalOpen(true);
  };

  return (
    <Box>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <MetricsCards submissions={submissions} jobs={jobs} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RateReviewOutlinedIcon fontSize="small" />}
          onClick={() => navigate("/jobs/competency-reviews")}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", boxShadow: "none" }}
        >
          Competency Reviews
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<VideocamOutlinedIcon fontSize="small" />}
          onClick={() => navigate("/jobs/videos")}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", boxShadow: "none" }}
        >
          Video Submissions
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreateJobModal}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "999px",
            boxShadow: "none",
            backgroundColor: "#614438",
            "&:hover": { backgroundColor: "#4d362c" },
          }}
        >
          New Job Posting
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          boxShadow: "none",
          p: 2.5,
          backgroundColor: colors.primary[900],
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={800} sx={{ color: colors.primary[100] }}>
          Applicants
        </Typography>
        <Typography variant="caption" sx={{ color: colors.primary[700], display: "block", mb: 2.5 }}>
          Click any row to view full candidate details
        </Typography>
        <ApplicantsTable submissions={submissions} jobs={jobs} onSelectSubmission={handleSelectSubmission} />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          boxShadow: "none",
          p: 2.5,
          backgroundColor: colors.primary[900],
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={800} sx={{ color: colors.primary[100] }}>
              Job Positions
            </Typography>
            <Typography variant="caption" sx={{ color: colors.primary[700] }}>
              Active job postings and their assessment pipelines
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon fontSize="small" />}
            onClick={openCreateJobModal}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "999px", boxShadow: "none" }}
          >
            Add Job
          </Button>
        </Box>

        <Grid container spacing={2}>
          {jobs
            .slice(
              jobsPagination.page * jobsPagination.pageSize,
              (jobsPagination.page + 1) * jobsPagination.pageSize,
            )
            .map((job) => (
            <Grid key={job.id} size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  borderRadius: "12px",
                  backgroundColor: colors.primary[800],
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <WorkOutlineIcon sx={{ fontSize: 18, color: "#614438" }} />
                    <Typography variant="body2" fontWeight={800} sx={{ color: colors.primary[100] }}>
                      {job.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Chip
                      status="neutral"
                      label={job.department}
                      sx={{ height: 22, backgroundColor: colors.primary[900], color: colors.primary[100], fontWeight: 700 }}
                    />
                    <Chip
                      status={job.active ? "success" : "neutral"}
                      label={job.active ? "Active" : "Inactive"}
                      onClick={() => handleToggleActive(job)}
                      sx={{ height: 22, cursor: "pointer" }}
                    />
                    <IconButton
                      size="small"
                      tooltip="Edit"
                      onClick={() => openEditJobModal(job)}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ color: colors.primary[700] }}>
                  {job.description}
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PeopleOutlineIcon sx={{ fontSize: 15, color: colors.primary[700] }} />
                    <Typography variant="caption" sx={{ color: colors.primary[700] }}>
                      {submissions.filter((sub) => sub.jobId === job.id).length} applicant
                      {submissions.filter((sub) => sub.jobId === job.id).length === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PlaceOutlinedIcon sx={{ fontSize: 15, color: colors.primary[700] }} />
                    <Typography variant="caption" sx={{ color: colors.primary[700] }}>
                      {job.location}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ScheduleOutlinedIcon sx={{ fontSize: 15, color: colors.primary[700] }} />
                    <Typography variant="caption" sx={{ color: colors.primary[700] }}>
                      {job.type}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ApartmentOutlinedIcon sx={{ fontSize: 15, color: colors.primary[700] }} />
                    <Typography variant="caption" sx={{ color: colors.primary[700] }}>
                      {job.workMode}
                    </Typography>
                  </Box>
                  {job.salaryRange && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AttachMoneyOutlinedIcon sx={{ fontSize: 15, color: colors.primary[700] }} />
                      <Typography variant="caption" sx={{ color: colors.primary[700] }}>
                        {job.salaryRange}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {[
                    { key: "candidateInfo" as const, label: "Candidate Information" },
                    { key: "personality" as const, label: "Personality Assessment" },
                    { key: "competency" as const, label: "Competency Test" },
                    { key: "videoPitch" as const, label: "Video Pitch" },
                  ]
                    .filter((stage) => job.assessmentSteps?.[stage.key])
                    .map((stage) => (
                      <Chip
                        key={stage.key}
                        status="info"
                        label={stage.label}
                        sx={{ height: 22, fontSize: 11, fontWeight: 700, color: "#fff", backgroundColor: "#0F4C81" }}
                      />
                    ))}
                </Box>
              </Box>
            </Grid>
            ))}
        </Grid>
        <Paginator count={jobs.length} pagination={jobsPagination} setPagination={setJobsPagination} />
      </Paper>

      <CandidateDetailModal
        submission={selectedSubmission}
        isOpen={isDetailModalOpen}
        onClose={handleCloseSubmission}
        onUpdateStatus={handleUpdateStatus}
      />

      <NewJobModal
        isOpen={isNewJobModalOpen}
        onClose={() => {
          setIsNewJobModalOpen(false);
          setEditingJob(null);
        }}
        onAddJob={handleAddJob}
        job={editingJob}
        onUpdateJob={handleUpdateJob}
        onToggleActive={handleToggleActive}
      />
    </Box>
  );
};

export default JobsAdminPage;
